// Onboarding Step 9 — explicit notification opt-in (2026-10-18).
// A TOP-LEVEL component (not defined inside OnboardingWizard): the wizard's inline Step components remount on every
// render, so local state / effects there loop. Nothing here runs on mount — saving and the OS permission request
// happen only on a button press.
// Nothing is pre-selected. "Not now" stores everything off. "Enable" stores exactly the selected categories, and only
// then (and only with at least one category) asks the OS permission. A failed save is shown as an error with retry —
// it is never treated as consent. Onboarding always continues.
import React, { useState } from 'react';
import { devicePermissionState, onboardingChoice, saveNotificationPrefs } from '../utils/notificationPrefs';
import { enablePushOnThisDevice } from '../firebase';
import { Bell } from '@phosphor-icons/react';

const DEVICE_MESSAGES = {
  registered: 'ההתראות הופעלו במכשיר הזה.',
  denied: 'ההרשאה להתראות חסומה במכשיר. הבחירה שלך נשמרה — כדי לקבל התראות, אפשר אותן בהגדרות הטלפון או הדפדפן ' +
    '(בסמסונג/Android: הגדרות ← אפליקציות ← RitmoIQ או Chrome ← התראות). אפשר לשנות גם בפרופיל.',
  dismissed: 'לא אישרת התראות במכשיר. הבחירה שלך נשמרה — אפשר לאפשר את ההתראות מהפרופיל בכל זמן.',
  unsupported: 'הדפדפן הזה לא תומך בהתראות. הבחירה שלך נשמרה ותחול במכשירים שתומכים בהן.',
  ios_needs_install: 'באייפון התראות עובדות רק מהאפליקציה המותקנת: שתף ← "הוסף למסך הבית", ואז פתח משם ' +
    'ואפשר התראות בפרופיל. הבחירה שלך נשמרה.',
  no_token: 'לא הצלחנו לרשום את המכשיר להתראות. הבחירה שלך נשמרה — נסה שוב מהפרופיל.',
  register_failed: 'לא הצלחנו לרשום את המכשיר להתראות. הבחירה שלך נשמרה — נסה שוב מהפרופיל.',
};

function Choice({ checked, onChange, title, children, testId }) {
  return (
    <label className="flex items-start gap-3 rounded-xl p-3 mb-3 cursor-pointer"
           style={{ background: 'var(--g-bg)', border: `1px solid ${checked ? 'var(--g-accent)' : 'var(--g-line)'}` }}>
      <input type="checkbox" className="mt-1 w-5 h-5" style={{ accentColor: 'var(--g-accent)' }} checked={checked} onChange={(e) => onChange(e.target.checked)}
             data-testid={testId} />
      <span className="flex-1 text-right">
        <span className="block text-text font-bold text-sm">{title}</span>
        <span className="block text-text-muted text-xs mt-1 leading-relaxed">{children}</span>
      </span>
    </label>
  );
}

export default function NotificationOptInStep({
  userId, apiBase, onContinue,
  savePrefs = saveNotificationPrefs, enableDevice = enablePushOnThisDevice, permissionState = devicePermissionState,
}) {
  const [training, setTraining] = useState(false);          // never pre-selected
  const [coachAnalysis, setCoachAnalysis] = useState(false);
  const [phase, setPhase] = useState('choose');              // choose | saving | save_error | result
  const [pending, setPending] = useState(null);              // 'enable' | 'decline' — what a retry repeats
  const [deviceMessage, setDeviceMessage] = useState('');

  const anySelected = training || coachAnalysis;

  const run = async (kind) => {
    setPending(kind);
    setPhase('saving');
    const prefs = kind === 'enable' ? onboardingChoice({ training, coachAnalysis }) : onboardingChoice({});
    try {
      await savePrefs(apiBase, userId, prefs);
    } catch {
      setPhase('save_error');                                // not consent: nothing requested, nothing registered
      return;
    }
    if (kind === 'decline' || !prefs.enabled) {
      onContinue();
      return;
    }
    const perm = permissionState();
    if (perm === 'unsupported' || perm === 'ios_needs_install') {
      setDeviceMessage(DEVICE_MESSAGES[perm]);
      setPhase('result');
      return;
    }
    let status = 'register_failed';
    try {
      status = (await enableDevice(userId)).status;          // the ONLY place the OS permission is requested
    } catch { /* keep register_failed */ }
    setDeviceMessage(DEVICE_MESSAGES[status] || DEVICE_MESSAGES.register_failed);
    setPhase('result');
  };

  const continueWithout = () => {
    try { localStorage.setItem(`notif-declined-${userId}`, 'true'); } catch { /* ignore */ }
    onContinue();
  };

  if (phase === 'result') {
    return (
      <div className="text-center" data-testid="notif-step-result">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed text-accent flex items-center justify-center mb-3"><Bell size={30} weight="bold" /></div>
        <p className="text-text text-sm leading-relaxed mb-5">{deviceMessage}</p>
        <button type="button" onClick={onContinue} className="w-full h-12 rounded-full btn-primary">המשך</button>
      </div>
    );
  }

  return (
    <div data-testid="notif-step">
      <div className="text-center mb-4">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed text-accent flex items-center justify-center mb-2"><Bell size={30} weight="bold" /></div>
        <h2 className="text-xl font-bold text-text">התראות</h2>
        <p className="text-text-muted text-sm mt-1">בחר אילו התראות תרצה לקבל. אפשר לשנות בכל זמן בפרופיל.</p>
      </div>

      <Choice checked={training} onChange={setTraining} title="התראות אימון" testId="opt-training">
        כרגע: התראת בוקר ביום מרוץ. תזכורות לאימונים מתוכננים ועדכונים על שינוי באימון עדיין לא זמינים.
      </Choice>
      <Choice checked={coachAnalysis} onChange={setCoachAnalysis} title="ניתוח המאמן" testId="opt-coach">
        התראה כשהניתוח של אימון שהסתיים מוכן. עבור Garmin ההתראות האלה עדיין לא מופעלות במערכת — הבחירה תישמר
        ותחול כשיופעלו.
      </Choice>

      {phase === 'save_error' && (
        <div className="rounded-xl p-3 mb-3 text-sm text-right" role="alert" data-testid="notif-save-error"
             style={{ background: 'var(--g-pressed)', border: '1px solid var(--g-line)', color: 'var(--g-sub)' }}>
          הבחירה לא נשמרה (בעיית תקשורת). לא הופעלו התראות.
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => run(pending)} className="flex-1 h-11 rounded-full btn-primary">נסה שוב</button>
            <button type="button" onClick={continueWithout} className="flex-1 h-11 rounded-full border border-border text-text-body">המשך בלי התראות</button>
          </div>
        </div>
      )}

      <button type="button" disabled={!anySelected || phase === 'saving'} onClick={() => run('enable')}
              className="w-full h-12 rounded-full font-bold mb-2"
              style={{ background: anySelected ? 'var(--g-accent)' : 'var(--g-bar)', color: anySelected ? 'var(--g-on-accent)' : 'var(--g-muted)',
                       opacity: phase === 'saving' ? 0.6 : 1 }}>
        {phase === 'saving' && pending === 'enable' ? 'שומר…' : 'הפעל את ההתראות שבחרתי'}
      </button>
      <button type="button" disabled={phase === 'saving'} onClick={() => run('decline')}
              className="w-full h-12 rounded-full border border-border text-text-body">
        {phase === 'saving' && pending === 'decline' ? 'שומר…' : 'לא עכשיו'}
      </button>
    </div>
  );
}
